import React from "react";
import moment from "moment";
import {
  HiOutlineExclamationTriangle,
  HiOutlineUsers,
  HiOutlineXMark,
} from "react-icons/hi2";
import { capitalizeAllLetters } from "Utils/utils";
import TimesheetStatusPill from "./TimesheetStatusPill";
import { formatDurationMinutes } from "./TimesheetTabPrimitives";

const resolveWorkerName = (shiftDetails, workerFallback) => {
  const worker =
    shiftDetails?.employee_profile ??
    shiftDetails?.employeeProfile ??
    shiftDetails?.worker ??
    workerFallback ??
    null;

  const firstName =
    worker?.first_name ??
    shiftDetails?.worker_first_name ??
    shiftDetails?.first_name;
  const lastName =
    worker?.last_name ??
    shiftDetails?.worker_last_name ??
    shiftDetails?.last_name;

  return { firstName, lastName };
};

const formatPersonDisplayName = (firstName, lastName) => {
  const parts = [firstName, lastName]
    .filter(Boolean)
    .map((name) => capitalizeAllLetters(String(name).trim()));
  return parts.join(" ").trim();
};

const formatMismatchHint = (shiftDetails) => {
  if (
    !shiftDetails?.start_time ||
    !shiftDetails?.worker_start_time ||
    shiftDetails.start_time === shiftDetails.worker_start_time
  ) {
    return null;
  }
  const sched = moment(shiftDetails.start_time, "YYYY-MM-DD HH:mm:ss");
  const actual = moment(shiftDetails.worker_start_time, "YYYY-MM-DD HH:mm:ss");
  if (!sched.isValid() || !actual.isValid()) return "Schedule vs actual differ";
  const diffMin = actual.diff(sched, "minutes");
  if (diffMin === 0) return null;
  if (diffMin > 0) {
    return `Start time ${formatDurationMinutes(diffMin)} late`;
  }
  return `Start time ${formatDurationMinutes(diffMin)} early`;
};

const TimesheetDetailHeader = ({
  shiftDetails,
  workerFallback,
  onClose,
}) => {
  if (!shiftDetails) return null;

  const { firstName, lastName } = resolveWorkerName(
    shiftDetails,
    workerFallback
  );
  const displayName = formatPersonDisplayName(firstName, lastName) || "Worker";

  const participant =
    shiftDetails?.participants?.[0] ?? shiftDetails?.participant ?? null;
  const participantName = participant
    ? formatPersonDisplayName(
      participant.first_name,
      participant.last_name
    )
    : null;

  const shiftDate = shiftDetails?.time_sheet_date
    ? moment(shiftDetails.time_sheet_date).format("ddd D MMM YYYY")
    : null;

  // const metaParts = [shiftDate, participantName].filter(Boolean);
  const metaParts = [shiftDate].filter(Boolean);

  const initials =
    `${(firstName || "").charAt(0)}${(lastName || "").charAt(0)}`.toUpperCase() ||
    displayName.slice(0, 2).toUpperCase();

  const scheduleMismatch =
    shiftDetails?.start_time &&
    shiftDetails?.worker_start_time &&
    (shiftDetails.start_time !== shiftDetails.worker_start_time ||
      shiftDetails.end_time !== shiftDetails.worker_end_time);

  const mismatchHint = scheduleMismatch ? formatMismatchHint(shiftDetails) : null;

  const showRatio =
    shiftDetails?.ratio && String(shiftDetails.ratio) !== "1:1";

  return (
    <div
      className="timesheet-detail-header flex items-center justify-between gap-2.5 border-b border-[#E5E7EB] px-5 py-2"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#4A6BB6] text-xs font-bold text-white"
        >
          {initials}
        </div>
        <div className="min-w-0 shrink">
          <p className="text-sm font-bold leading-tight text-[#0F172A]">
            {displayName}
          </p>
          {metaParts.length > 0 && (
            <p className="text-[11px] font-medium leading-snug text-[#64748B]">
              {metaParts.join(" · ")}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {shiftDetails?.status && (
            <TimesheetStatusPill status={shiftDetails.status} />
          )}
          {mismatchHint && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[11px] font-semibold text-[#D97706]"
            >
              <HiOutlineExclamationTriangle className="h-3 w-3 shrink-0" />
              {mismatchHint}
            </span>
          )}
          {showRatio && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-[#EEF2FB] px-2 py-0.5 text-[11px] font-semibold text-[#4A6BB6]"
            >
              <HiOutlineUsers className="h-2.5 w-2.5 shrink-0" />
              Ratio {shiftDetails.ratio}
            </span>
          )}
        </div>
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border border-[#E5E7EB] bg-[#F8FAFC] text-[#64748B] transition-colors hover:bg-[#EEF2FB] hover:text-[#4A6BB6]"
          aria-label="Close detail panel"
        >
          <HiOutlineXMark className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default TimesheetDetailHeader;
